import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/config";
import { Printer, Loader2, AlertCircle } from "lucide-react";

export default function ReportPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/reports/${id}`).then((res) => setR(res.data)).catch(() => setR(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-40 text-center font-mono-tech text-white/60"><Loader2 className="inline animate-spin mr-2" size={16} />Caricamento...</div>;
  if (!r) return <div className="pt-40 text-center"><AlertCircle className="mx-auto mb-4 text-red-400" size={40} /><div className="font-display text-3xl">Rapporto non trovato</div></div>;

  return (
    <div className="pt-24 pb-24">
      <style>{`@media print{header,footer,.no-print{display:none!important}body{background:#fff!important;color:#000!important}.text-white,.text-white\\/70,.text-white\\/60,.text-white\\/75,.text-white\\/80,.text-white\\/50{color:#000!important}.border-\\[\\#1a1a1a\\],.border-\\[\\#222\\]{border-color:#ccc!important}.bg-\\[\\#0a0a0a\\]{background:#f8f8f8!important}}`}</style>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="num-chip mb-4"><span className="dot" /> Rapporto Intervento · {id.slice(0, 8).toUpperCase()}</div>
            <div className="font-display text-4xl md:text-5xl leading-[0.95]">Gefion S.r.l.</div>
            <div className="font-mono-tech text-white/50 text-[11px] mt-3">Via 1° Maggio 6, 33070 Budoia (PN) · P.IVA 01675990939 · PEC gefionsrl@legalmail.it</div>
          </div>
          <button onClick={() => window.print()} className="btn-ghost no-print"><Printer size={14} /> Stampa</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="border border-[#1a1a1a] p-5">
            <div className="font-mono-tech text-[10px] text-white/40 mb-3">Cliente</div>
            <div className="font-display text-lg">{r.customer_name || "—"}</div>
            <div className="text-white/70 text-sm mt-1">{r.customer_email || "—"}</div>
            {r.plant_name && <div className="text-white/60 text-sm mt-2">📍 {r.plant_name}</div>}
          </div>
          <div className="border border-[#1a1a1a] p-5">
            <div className="font-mono-tech text-[10px] text-white/40 mb-3">Intervento</div>
            <div className="font-mono-tech text-[12px]">Data: <span className="text-white">{r.intervention_date}</span></div>
            <div className="font-mono-tech text-[12px]">Tecnico: <span className="text-white">{r.technician_name}</span></div>
            <div className="font-mono-tech text-[12px]">Divisione: <span className="text-white">{r.area || "—"}</span></div>
            <div className="font-mono-tech text-[12px] mt-2">Copertura: <span className={r.is_covered_by_contract ? "text-[#22c55e]" : "text-[#ff4b00]"}>{r.is_covered_by_contract ? "In contratto" : "A pagamento"}</span></div>
          </div>
        </div>

        <div className="border border-[#1a1a1a] p-6 mb-6">
          <div className="font-mono-tech text-[10px] text-white/40 mb-3">Lavoro eseguito</div>
          <div className="whitespace-pre-wrap text-white/85 text-[15px] leading-relaxed">{r.work_description}</div>
        </div>

        {r.spare_parts?.length > 0 && (
          <div className="border border-[#1a1a1a] mb-6">
            <div className="font-mono-tech text-[10px] text-white/40 p-4 border-b border-[#1a1a1a]">Ricambi utilizzati</div>
            <table className="w-full text-left text-[13px]">
              <thead className="bg-[#0a0a0a] text-white/60 font-mono-tech text-[10px]"><tr><th className="p-3">Descrizione</th><th className="p-3 text-right">Q.tà</th><th className="p-3 text-right">Prezzo unitario</th><th className="p-3 text-right">Totale</th></tr></thead>
              <tbody>
                {r.spare_parts.map((sp, i) => (
                  <tr key={i} className="border-t border-[#151515]">
                    <td className="p-3 text-white/85">{sp.name}</td>
                    <td className="p-3 text-right font-mono-tech">{sp.qty}</td>
                    <td className="p-3 text-right font-mono-tech">€ {sp.unit_price.toFixed(2)}</td>
                    <td className="p-3 text-right font-mono-tech">€ {(sp.qty * sp.unit_price).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(r.hours_worked > 0 || r.travel_km > 0) && (
          <div className="border border-[#1a1a1a] p-6 mb-6">
            <div className="font-mono-tech text-[10px] text-white/40 mb-3">Manodopera e trasferta</div>
            <div className="grid grid-cols-2 gap-y-2 font-mono-tech text-[12px]">
              {r.hours_worked > 0 && <><div className="text-white/50">Ore lavorate</div><div className="text-right">{r.hours_worked}h × €{r.hourly_rate}/h = <span className="text-white">€ {r.totals.labour_total.toFixed(2)}</span></div></>}
              {r.travel_km > 0 && <><div className="text-white/50">Trasferta</div><div className="text-right">{r.travel_km} km × €{r.travel_rate_per_km}/km = <span className="text-white">€ {r.totals.travel_total.toFixed(2)}</span></div></>}
            </div>
          </div>
        )}

        <div className="border border-[#ff4b00]/50 p-6 bg-[#ff4b00]/5">
          <div className="font-mono-tech text-[10px] text-[#ff4b00] mb-3">Riepilogo importi</div>
          <div className="space-y-2 font-mono-tech text-[13px]">
            {r.totals.parts_total > 0 && <div className="flex justify-between"><span className="text-white/50">Ricambi</span><span>€ {r.totals.parts_total.toFixed(2)}</span></div>}
            {r.totals.labour_total > 0 && <div className="flex justify-between"><span className="text-white/50">Manodopera</span><span>€ {r.totals.labour_total.toFixed(2)}</span></div>}
            {r.totals.travel_total > 0 && <div className="flex justify-between"><span className="text-white/50">Trasferta</span><span>€ {r.totals.travel_total.toFixed(2)}</span></div>}
            <div className="flex justify-between border-t border-[#ff4b00]/30 pt-2 mt-2"><span className="text-white/70">Totale netto</span><span className="text-white">€ {r.totals.net_total.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-white/50">IVA 22%</span><span>€ {r.totals.vat_amount.toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-[#ff4b00]/30 pt-2 mt-2"><span className="font-display text-lg text-white">Totale (IVA incl.)</span><span className="font-display text-2xl text-[#ff4b00]">€ {r.totals.gross_total.toFixed(2)}</span></div>
          </div>
          {r.is_covered_by_contract && <div className="mt-4 font-mono-tech text-[10px] text-[#22c55e]">✓ Intervento coperto da contratto — importo indicativo, non fatturato al cliente</div>}
        </div>

        {r.notes && (
          <div className="border border-[#1a1a1a] p-5 mt-6">
            <div className="font-mono-tech text-[10px] text-white/40 mb-2">Note aggiuntive</div>
            <div className="text-white/75 text-sm whitespace-pre-wrap">{r.notes}</div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-[#1a1a1a] font-mono-tech text-[10px] text-white/40 text-center">
          Rapporto emesso il {new Date(r.created_at).toLocaleDateString("it-IT")} · Gefion S.r.l.
        </div>
      </div>
    </div>
  );
}
